// Multievento en las pantallas de la ficha (lote 9a de la auditoría): costes
// por menú calculados jornada a jornada (M-44), adaptaciones dietéticas de
// cada jornada (M-65) y la distribución de menús del gestor de jornadas sin
// el concepto de "contratado" (M-66). Funciones puras de solo lectura: no
// guardan nada. Un evento de un día da lo mismo que antes.
import {
  claveLinea, copiaParaJornada, esMultievento, eventoPasado, jornadasOrdenadas, lineaCabecera, lineaResuelta,
} from './multieventoCabecera.js';

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Línea de una jornada completa para calcular sus costes: lo de su línea de la
// cabecera (configuración de personal, bebida, ratios...) salvo lo propio del
// día, con lo de la jornada encima, el precio de la cabecera y sus platos o,
// si no tiene, los de la cabecera (mismo criterio que el servidor:
// linea_completa_para_jornada y platos_de_linea_jornada)
export const lineaCompletaJornada = (linea, par, raiz = []) => {
  if (!linea || !par) return linea;
  const mezcla = { ...copiaParaJornada(par, linea.num_personas), ...linea };
  const res = lineaResuelta(mezcla, raiz);
  if (!(linea.platos_ids || []).length && (par.platos_ids || []).length) {
    res.platos_ids = [...par.platos_ids];
    const propias = linea.unidades_por_plato && Object.keys(linea.unidades_por_plato).length > 0
      ? linea.unidades_por_plato : null;
    res.unidades_por_plato = propias || par.unidades_por_plato || {};
  }
  return res;
};

// Líneas de un multievento para los costes, jornada a jornada en orden de
// fecha: [{ jornadaId, numero, personas, linea, lineaCab }]. `linea` va
// completa (lineaCompletaJornada); `lineaCab` es su línea de la cabecera o
// null si solo está en la jornada (alquiler o bebida de la jornada, por ejemplo)
export const lineasCostesMultievento = (p) => {
  if (!esMultievento(p)) return [];
  const raiz = (p.menus_personalizados || []).filter(Boolean);
  const res = [];
  jornadasOrdenadas(p).forEach((j, idx) => {
    (Array.isArray(j.menus_personalizados) ? j.menus_personalizados : [])
      .filter(l => l && typeof l === 'object')
      .forEach((l) => {
        const par = lineaCabecera(l, raiz);
        res.push({
          jornadaId: j.id,
          numero: idx + 1,
          personas: num(l.num_personas),
          linea: par ? lineaCompletaJornada(l, par, raiz) : l,
          lineaCab: par || null,
        });
      });
  });
  return res;
};

// Líneas de las jornadas que corresponden a una línea de la cabecera (vacío si
// no es multievento o la línea no está en ninguna jornada)
export const lineasJornadaDeCabecera = (lineaCab, p) => (
  lineaCab ? lineasCostesMultievento(p).filter(x => x.lineaCab === lineaCab) : []
);

// Líneas que solo están en las jornadas (sin línea de la cabecera)
export const lineasSoloJornadas = (p) => lineasCostesMultievento(p).filter(x => !x.lineaCab);

// Personas de una línea de la cabecera para repartir sus costes por persona:
// en un multievento, la suma de las jornadas (20 + 35 = 55); si no está en
// ninguna jornada o es un evento de un día, las de la línea
export const personasLineaCabecera = (lineaCab, p) => {
  const deJornadas = lineasJornadaDeCabecera(lineaCab, p);
  if (deJornadas.length === 0) return num(lineaCab?.num_personas);
  return deJornadas.reduce((s, x) => s + x.personas, 0);
};

const CAMPOS_COSTE = ['costeIngredientes', 'costeMaterialElaboracion', 'costePersonalCocina',
  'costePersonalEvento', 'costeTotal'];
const GRUPOS_PERSONAL = ['personalPropio', 'camareros', 'extras'];

// Suma de desgloses de coste de varias líneas (las de cada jornada)
export const sumarDesglosesCoste = (lista) => {
  const res = {};
  CAMPOS_COSTE.forEach((c) => { res[c] = 0; });
  GRUPOS_PERSONAL.forEach((g) => { res[g] = { cantidad: 0, coste: 0 }; });
  (lista || []).filter(Boolean).forEach((d) => {
    CAMPOS_COSTE.forEach((c) => { res[c] += num(d[c]); });
    GRUPOS_PERSONAL.forEach((g) => {
      res[g].cantidad += num(d[g]?.cantidad);
      res[g].coste += num(d[g]?.coste);
    });
  });
  return res;
};

// Adaptaciones dietéticas del Resumen y de Por hacer (M-65). En un
// multievento, las de cada jornada con su etiqueta ("2 sin gluten (J2)"); la
// cabecera no cuenta (salvo un multievento antiguo sin líneas en las
// jornadas). Las generadas por la pestaña Alérgenos se cuentan en esa pestaña.
// { total, conNombre, resumen }
export const resumenAdaptaciones = (p) => {
  const jornadas = esMultievento(p) ? jornadasOrdenadas(p) : [];
  const conLineas = jornadas.some(j => (j.menus_personalizados || []).length > 0);
  const fuentes = conLineas
    ? jornadas.map((j, idx) => ({ etiqueta: `J${idx + 1}`, lineas: j.menus_personalizados || [] }))
    : [{ etiqueta: null, lineas: p?.menus_personalizados || [] }];
  const porTipo = new Map();
  let total = 0;
  let conNombre = 0;
  fuentes.forEach(({ etiqueta, lineas }) => {
    lineas.filter(Boolean).forEach((menu) => {
      (menu.adaptaciones || []).forEach((a) => {
        if (!a || a.origen === 'pestana_alergenos') return;
        total += 1;
        const tipo = String(a.tipo || 'adaptación').replace(/_/g, ' ');
        const clave = `${tipo}|${etiqueta || ''}`;
        const actual = porTipo.get(clave) || { tipo, etiqueta, n: 0 };
        actual.n += num(a.num_personas);
        porTipo.set(clave, actual);
        conNombre += (a.nombres_personas || []).filter(Boolean).length;
      });
    });
  });
  const resumen = [...porTipo.values()]
    .map(({ tipo, etiqueta, n }) => `${n} ${tipo}${etiqueta ? ` (${etiqueta})` : ''}`)
    .join(' · ');
  return { total, conNombre, resumen };
};

// Número de adaptaciones de una línea de la cabecera: en un multievento, las
// de sus líneas en las jornadas; si no, las suyas
export const numAdaptacionesLinea = (lineaCab, p) => {
  const deJornadas = lineasJornadaDeCabecera(lineaCab, p);
  if (deJornadas.length === 0) return (lineaCab?.adaptaciones || []).length;
  return deJornadas.reduce((s, x) => s + (x.linea?.adaptaciones || []).length, 0);
};

// "20 (J1) + 35 (J2) = 55", "35 (J2)" o "sin asignar"
export const textoAsignadoJornadas = (porJornada) => {
  const lista = Array.isArray(porJornada) ? porJornada : [];
  if (lista.length === 0) return 'sin asignar';
  const partes = lista.map(x => `${x.uds} (J${x.numero})`).join(' + ');
  if (lista.length === 1) return partes;
  return `${partes} = ${lista.reduce((s, x) => s + x.uds, 0)}`;
};

// Distribución de los menús de la cabecera en las jornadas (M-66): sin
// "contratado" ni disponible; solo lo asignado en cada jornada.
// { [claveLinea]: { nombre, porJornada: [{ numero, uds }], asignado, texto } }
export const distribucionMenusJornadas = (raiz, jornadas) => {
  const lineas = (raiz || []).filter(Boolean);
  const dist = {};
  lineas.forEach((m) => {
    dist[claveLinea(m)] = { nombre: m.nombre_menu, porJornada: [], asignado: 0, texto: '' };
  });
  jornadasOrdenadas({ jornadas: jornadas || [] }).forEach((j, idx) => {
    const porClave = {};
    (j.menus_personalizados || []).filter(Boolean).forEach((l) => {
      const k = claveLinea(lineaResuelta(l, lineas));
      if (dist[k]) porClave[k] = (porClave[k] || 0) + num(l.num_personas);
    });
    Object.entries(porClave).forEach(([k, uds]) => {
      dist[k].porJornada.push({ numero: idx + 1, uds });
      dist[k].asignado += uds;
    });
  });
  Object.values(dist).forEach((d) => { d.texto = textoAsignadoJornadas(d.porJornada); });
  return dist;
};

// Personas del gestor de jornadas: "55 personas en total, de 20 a 35 por
// jornada" (o "60 personas en total, 30 por jornada")
export const textoPersonasGestorJornadas = (jornadas) => {
  const pax = (jornadas || []).filter(Boolean).map(j => num(j.num_personas)).filter(n => n > 0);
  const total = pax.reduce((s, n) => s + n, 0);
  const base = `${total} ${total === 1 ? 'persona' : 'personas'} en total`;
  if (pax.length <= 1) return base;
  const min = Math.min(...pax);
  const max = Math.max(...pax);
  return min === max ? `${base}, ${min} por jornada` : `${base}, de ${min} a ${max} por jornada`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Pestaña Líneas y menús del formato nuevo: gestión de las jornadas (crear,
// editar, menús, duplicar, eliminar) sin el gestor heredado. Funciones puras:
// preparan lo que se envía al servidor, que es quien guarda y recalcula.
// ─────────────────────────────────────────────────────────────────────────────

// Campos de lugar y ruta de una jornada: se vacían para volver a la dirección
// principal (mismo conjunto que logistica2/LogisticaJornada.js)
export const CAMPOS_LUGAR_JORNADA = [
  'lugar_evento', 'direccion_exacta', 'coordenadas', 'place_id', 'municipio',
  'distancia_km', 'tiempo_viaje_min', 'coste_transporte',
];

const txt = (v) => String(v ?? '').trim();
const vacio = (v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
const fechaDe = (v) => String(v ?? '').slice(0, 10);

// Líneas de una jornada con las unidades cambiadas en línea: `cambios` es
// { [posición]: uds } sobre la lista sin huecos. Solo cambian las personas de
// esas posiciones (enteros mayores que 0); el resto de la línea, tal cual (el
// precio y el IVA siguen a la cabecera en el servidor).
export const lineasConUdsCambiadas = (lineas, cambios = {}) => (
  (Array.isArray(lineas) ? lineas : []).filter(Boolean).map((l, pos) => {
    if (!Object.prototype.hasOwnProperty.call(cambios || {}, pos)) return l;
    const n = parseInt(cambios[pos], 10);
    if (!Number.isFinite(n) || n <= 0 || n === num(l.num_personas)) return l;
    return { ...l, num_personas: n };
  })
);

// ¿Cambia el lugar de la jornada? (texto, dirección, sitio de Google o coordenadas)
const lugarDistinto = (a, b) => txt(a.lugar_evento) !== txt(b.lugar_evento)
  || txt(a.direccion_exacta) !== txt(b.direccion_exacta)
  || txt(a.place_id) !== txt(b.place_id)
  || JSON.stringify(a.coordenadas || null) !== JSON.stringify(b.coordenadas || null);

// Cuerpo del PUT de una jornada con SOLO lo que cambia entre la jornada
// guardada y lo editado en el modal (`borrar` para vaciar los campos que lo
// admiten). Lugar vacío o `usarPrincipal`: la jornada vuelve a la dirección
// principal (se vacían sus campos de lugar y ruta). La fecha y las horas de
// inicio y fin no se pueden vaciar. null si no cambia nada.
export const cuerpoActualizacionJornada = (original, editado) => {
  const o = original || {};
  const e = editado || {};
  const cuerpo = {};
  const borrar = [];
  if (!vacio(e.fecha) && fechaDe(e.fecha) !== fechaDe(o.fecha)) cuerpo.fecha = fechaDe(e.fecha);
  ['hora_inicio', 'hora_fin'].forEach((c) => {
    if (!vacio(e[c]) && txt(e[c]) !== txt(o[c])) cuerpo[c] = txt(e[c]);
  });
  ['hora_llegada_estimada', 'detalles_acceso'].forEach((c) => {
    if (txt(e[c]) === txt(o[c])) return;
    if (vacio(e[c])) borrar.push(c);
    else cuerpo[c] = txt(e[c]);
  });
  if (e.observaciones !== undefined && txt(e.observaciones) !== txt(o.observaciones)) {
    cuerpo.observaciones = String(e.observaciones ?? '');
  }
  const heredaAntes = vacio(o.lugar_evento) && vacio(o.direccion_exacta);
  const heredaNuevo = !!e.usarPrincipal || vacio(e.lugar_evento);
  if (heredaNuevo) {
    if (!heredaAntes) borrar.push(...CAMPOS_LUGAR_JORNADA);
  } else if (heredaAntes || lugarDistinto(e, o)) {
    CAMPOS_LUGAR_JORNADA.forEach((c) => {
      const v = e[c];
      if (vacio(v)) {
        borrar.push(c);
      } else if (c === 'distancia_km' || c === 'coste_transporte') {
        cuerpo[c] = Number(v);
      } else if (c === 'tiempo_viaje_min') {
        cuerpo[c] = parseInt(v, 10) || 0;
      } else {
        cuerpo[c] = typeof v === 'string' ? v.trim() : v;
      }
    });
  }
  if (borrar.length > 0) cuerpo.borrar = [...new Set(borrar)];
  return Object.keys(cuerpo).length > 0 ? cuerpo : null;
};

const minutosDe = (h) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(txt(h));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

// Horas de una jornada: { texto: '22:00 a 02:00', masUno: true } (termina al
// día siguiente si la hora de fin es anterior a la de inicio)
export const horasJornada = (j) => {
  const ini = txt(j?.hora_inicio);
  const fin = txt(j?.hora_fin);
  if (!ini && !fin) return { texto: '', masUno: false };
  if (!ini) return { texto: `hasta ${fin}`, masUno: false };
  if (!fin) return { texto: `desde ${ini}`, masUno: false };
  const a = minutosDe(ini);
  const b = minutosDe(fin);
  return { texto: `${ini} a ${fin}`, masUno: a !== null && b !== null && b < a };
};

// Ids de las jornadas sin líneas: lo que avisa el servidor (`jornadas_vacias`
// del GET) o, si no viene, las jornadas sin ninguna línea (un evento pasado no
// se avisa, como en el servidor)
export const idsJornadasVacias = (p) => {
  if (Array.isArray(p?.jornadas_vacias)) {
    return new Set(p.jornadas_vacias.map(x => x && x.jornada_id).filter(Boolean));
  }
  if (!esMultievento(p) || eventoPasado(p)) return new Set();
  return new Set(p.jornadas
    .filter(j => j && (Array.isArray(j.menus_personalizados) ? j.menus_personalizados : []).filter(Boolean).length === 0)
    .map(j => j.id));
};

// ¿Tiene ya la jornada (lista en edición) la línea de la cabecera?
export const jornadaTieneLinea = (lineas, lineaCab, raiz = []) => (
  !!lineaCab && (Array.isArray(lineas) ? lineas : []).some(l => l && lineaCabecera(l, raiz) === lineaCab)
);

// "Añadir a esta jornada" una línea de la cabecera: su copia (precio, IVA,
// concepto, bebida y personal de la cabecera) con las personas indicadas. Si
// la jornada ya la tiene, la lista no cambia.
export const anadirLineaCabeceraAJornada = (lineas, lineaCab, personas, raiz = []) => {
  const lista = (Array.isArray(lineas) ? lineas : []).filter(Boolean);
  if (!lineaCab || jornadaTieneLinea(lista, lineaCab, raiz)) return lista;
  return [...lista, copiaParaJornada(lineaCab, Math.max(1, parseInt(personas, 10) || 1))];
};

// Horas por defecto de una jornada nueva: las de la última jornada por fecha
// (o 09:00 a 14:00)
export const horasPorDefectoJornadaNueva = (p) => {
  const lista = jornadasOrdenadas(p);
  const ultima = lista.length > 0 ? lista[lista.length - 1] : null;
  return {
    hora_inicio: txt(ultima?.hora_inicio) || '09:00',
    hora_fin: txt(ultima?.hora_fin) || '14:00',
  };
};

const DIAS_SEMANA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];

// "Jornada 2 (martes 18/11/2026)" para las confirmaciones
export const textoJornada = (j, numero) => {
  const f = fechaDe(j?.fecha);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f)) return `Jornada ${numero}`;
  const dia = DIAS_SEMANA[new Date(`${f}T12:00:00`).getDay()];
  return `Jornada ${numero} (${dia} ${f.slice(8, 10)}/${f.slice(5, 7)}/${f.slice(0, 4)})`;
};
